// T3-B PR 4-bis (2026-05-12):
//   tier プリセットの create / edit (= tier_definitions 順序付き配列 editor 含む)。
//
// Spec: design/sponsored-motif-spec.md v3 §6.7 / §4.2
//
// 簡略化: tier_definitions の reorder UI は本 PR ではスキップ (= 編集順 = 入力順)。
// 各 tier の value / label / weight / color のみ編集可。drag&drop reorder は将来追加。

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Trash2, Plus } from 'lucide-react';

interface TierDef {
    value: number,
    label: string,
    weight: number,
    color?: string | null
}

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    preset: any | null,
    onSuccess?: () => void
}

const DEFAULT_TIER: TierDef = { value: 1, label: '', weight: 100, color: '#888888' };
const SEED_PRESET_KEYS = ['omikuji_preset', 'bingo_preset', 'gacha_preset', 'rank_preset', 'stamp_preset'];

export default observer(function TierPresetFormSheet({ open, onOpenChange, mode, preset, onSuccess }: Props) {
    const {
        tierCatalogStore: { createTierPreset, updateTierPreset }
    } = useStore();

    const [presetKey, setPresetKey] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [definitions, setDefinitions] = useState<TierDef[]>([{ ...DEFAULT_TIER }]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && preset) {
            setPresetKey(preset.preset_key ?? '');
            setName(preset.name ?? '');
            setDescription(preset.description ?? '');
            setIsActive(!!preset.is_active);
            const defs = Array.isArray(preset.tier_definitions) ? preset.tier_definitions : [];
            setDefinitions(defs.length > 0 ?
                defs.map((d: any) => ({
                    value: Number(d.value) || 1,
                    label: String(d.label || ''),
                    weight: Number(d.weight ?? 0),
                    color: d.color ?? null
                })) :
                [{ ...DEFAULT_TIER }]);
        } else {
            setPresetKey('');
            setName('');
            setDescription('');
            setIsActive(true);
            setDefinitions([{ ...DEFAULT_TIER }]);
        }
    }, [open, mode, preset]);

    const updateDef = (idx: number, patch: Partial<TierDef>) => {
        setDefinitions(d => d.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    const addDef = () => {
        const next = definitions.length > 0 ? Math.max(...definitions.map(d => d.value)) + 1 : 1;
        setDefinitions(d => [...d, { ...DEFAULT_TIER, value: next, weight: 0 }]);
    };

    const removeDef = (idx: number) => {
        setDefinitions(d => d.filter((_, i) => i !== idx));
    };

    const isSeed = mode === 'edit' && SEED_PRESET_KEYS.includes(preset?.preset_key);

    const handleSubmit = async () => {
        if (!presetKey.trim() || !name.trim()) {
            toastify('error', 'preset_key と name は必須');
            return;
        }
        if (definitions.length === 0) {
            toastify('error', 'tier_definitions は最低 1 件必要');
            return;
        }
        const values = new Set<number>();
        for (const d of definitions) {
            if (!d.label.trim()) {
                toastify('error', 'tier label は必須');
                return;
            }
            if (values.has(d.value)) {
                toastify('error', `tier value=${d.value} が重複`);
                return;
            }
            values.add(d.value);
        }
        const payload: any = {
            preset_key: presetKey,
            name,
            description: description || null,
            tier_definitions: definitions.map(d => ({
                value: Number(d.value),
                label: d.label,
                weight: Number(d.weight ?? 0),
                color: d.color || null
            })),
            is_active: isActive
        };
        setSubmitting(true);
        try {
            const res = mode === 'create' ?
                await flowResult(createTierPreset(payload)) :
                await flowResult(updateTierPreset({ id: preset.id, data: payload }));
            if (res?.ok) {
                toastify('success', mode === 'create' ? '作成しました' : '更新しました');
                onOpenChange(false);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[640px] sm:max-w-[640px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'tier プリセット新規作成' : 'tier プリセット編集'}</SheetTitle>
                    <SheetDescription>
                        排出ヒエラルキー定義 (例: 大吉/中吉/...、当たり/ハズレ)。weight は排出確率の重み。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div>
                        <Label>
                            preset_key
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={presetKey}
                            onChange={e => setPresetKey(e.target.value)}
                            placeholder='例: spring_omikuji_preset'
                            disabled={isSeed}
                        />
                        {isSeed && <p className='text-xs text-amber-700 mt-1'>seed preset_key は rename 不可</p>}
                    </div>

                    <div>
                        <Label>
                            表示名
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder='例: 春のおみくじ 5 段'
                        />
                    </div>

                    <div>
                        <Label>説明</Label>
                        <Textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={2}
                            placeholder='想定用途 (任意)'
                        />
                    </div>

                    <div>
                        <div className='flex items-center justify-between mb-2'>
                            <Label>tier 定義 (順序付き)</Label>
                            <Button type='button' variant='outline' size='sm' onClick={addDef}>
                                <Plus className='w-4 h-4 mr-1' />
                                追加
                            </Button>
                        </div>
                        <div className='space-y-2'>
                            {definitions.map((d, idx) => (
                                <div key={idx} className='grid grid-cols-[60px_1fr_80px_60px_40px] gap-2 items-center border border-gray-200 rounded p-2'>
                                    <Input
                                        type='number'
                                        value={d.value}
                                        onChange={e => updateDef(idx, { value: Number(e.target.value) })}
                                        placeholder='value'
                                    />
                                    <Input
                                        value={d.label}
                                        onChange={e => updateDef(idx, { label: e.target.value })}
                                        placeholder='label (例: 大吉)'
                                    />
                                    <Input
                                        type='number'
                                        step='0.01'
                                        value={d.weight}
                                        onChange={e => updateDef(idx, { weight: Number(e.target.value) })}
                                        placeholder='weight'
                                    />
                                    <Input
                                        value={d.color ?? ''}
                                        onChange={e => updateDef(idx, { color: e.target.value })}
                                        placeholder='#RRGGBB'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        onClick={() => removeDef(idx)}
                                        disabled={definitions.length <= 1}
                                        className='text-red-600'
                                    >
                                        <Trash2 className='w-4 h-4' />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <p className='text-xs text-gray-500 mt-2'>
                            weight 合計が抽選の総和。実装上は値の比率で正規化される。
                        </p>
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            id='is_active'
                        />
                        <Label htmlFor='is_active'>is_active (= 公開)</Label>
                    </div>
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        キャンセル
                    </Button>
                    <Button type='button' variant='active' onClick={handleSubmit} disabled={submitting}>
                        {mode === 'create' ? '作成' : '更新'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
